import type { ToolId } from '@/types';

/**
 * A small animation of what each tool actually does — cells landing in a
 * grid, one picture coming apart into tiles, a shape running straight
 * through slide seams.
 *
 * Every piece plays an entrance once and then settles into a loop that
 * returns to where it started. That is what makes the loops seamless, and it
 * is also what makes them safe under `prefers-reduced-motion`, where the
 * browser jumps straight to each animation's last frame: what lands there is
 * the finished drawing.
 */
export function ToolArt({ tool }: { tool: ToolId }) {
  if (tool === 'collage') return <CollageArt />;
  if (tool === 'splitter') return <SplitterArt />;
  return <CarouselArt />;
}

const box = 'h-14 w-[76px] overflow-visible';

/** Two cells over one merged cell — the layout the tool opens on. */
function CollageArt() {
  const cells = [
    { x: 2, y: 2, w: 28, h: 20, rest: 0.85 },
    { x: 34, y: 2, w: 28, h: 20, rest: 0.5 },
    { x: 2, y: 26, w: 60, h: 20, rest: 0.68 },
  ];
  return (
    <svg viewBox="0 0 64 48" className={box} aria-hidden>
      {cells.map((c, i) => (
        <rect
          key={i}
          className="art-piece"
          x={c.x}
          y={c.y}
          width={c.w}
          height={c.h}
          rx="3"
          fill="var(--accent-solid)"
          style={{
            // @ts-expect-error — a custom property the keyframes read.
            '--rest': c.rest,
            animation: `art-assemble 460ms var(--ease) both, collage-breathe 3.4s ease-in-out infinite`,
            animationDelay: `${i * 110}ms, ${600 + i * 380}ms`,
          }}
        />
      ))}
    </svg>
  );
}

/**
 * One gradient spanning all nine tiles, so the picture only reads as whole
 * while they are together — which is the entire point of the tool.
 */
function SplitterArt() {
  const cols = 3;
  const rows = 3;
  return (
    <svg viewBox="0 0 64 48" className={box} aria-hidden>
      <defs>
        <linearGradient id="art-split" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--accent-solid)" stopOpacity=".95" />
          <stop offset="100%" stopColor="var(--accent-solid)" stopOpacity=".42" />
        </linearGradient>
      </defs>
      {Array.from({ length: rows }, (_, r) =>
        Array.from({ length: cols }, (_, c) => (
          <rect
            key={`${r}-${c}`}
            className="art-piece"
            x={2 + c * 21}
            y={2 + r * 15.4}
            width="19"
            height="13.4"
            rx="2.5"
            fill="url(#art-split)"
            style={{
              // Each tile pushes away from the middle of the grid.
              // @ts-expect-error — custom properties the keyframes read.
              '--dx': `${(c - 1) * 2.4}px`,
              '--dy': `${(r - 1) * 2.4}px`,
              animation: `art-assemble 420ms var(--ease) both, split-apart 4.2s var(--ease) infinite`,
              animationDelay: `${(r + c) * 70}ms, ${900 + (r + c) * 40}ms`,
            }}
          />
        )),
      )}
    </svg>
  );
}

/** One continuous strip, cut by marching seams, with a curve crossing them. */
function CarouselArt() {
  // The panels touch: the strip really is continuous, and the dashed cuts are
  // the only thing dividing it — which is the idea the tool is built on.
  const panels = [2, 22, 42];
  const seams = [22, 42];
  return (
    <svg viewBox="0 0 64 48" className={box} aria-hidden>
      <defs>
        <linearGradient id="art-slide" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--accent-solid)" stopOpacity=".34" />
          <stop offset="100%" stopColor="var(--accent-solid)" stopOpacity=".2" />
        </linearGradient>
      </defs>

      {panels.map((x, i) => (
        <rect
          key={x}
          className="art-piece"
          x={x}
          y="4"
          width="20"
          height="40"
          fill="url(#art-slide)"
          style={{
            animation: 'art-assemble 440ms var(--ease) both',
            animationDelay: `${i * 110}ms`,
          }}
        />
      ))}

      {/* The cuts. They march rather than travel, so the loop has no restart. */}
      {seams.map((x) => (
        <line
          key={x}
          x1={x}
          y1="4"
          x2={x}
          y2="44"
          stroke="var(--accent)"
          strokeWidth="1.4"
          strokeDasharray="4 4"
          style={{ animation: 'seam-march 900ms linear infinite' }}
        />
      ))}

      {/* The element that carries on into the next slide. It draws itself
          across the seams once the strip has landed. */}
      <path
        d="M6 34 C 20 12, 44 38, 60 15"
        stroke="var(--accent-solid)"
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
        style={{
          // Longer than the path, so the whole curve is a single dash.
          // @ts-expect-error — a custom property the keyframes read.
          '--len': '90',
          strokeDasharray: 90,
          animation: 'curve-draw 900ms var(--ease) both',
          animationDelay: '420ms',
        }}
      />
    </svg>
  );
}
